/**
 * Purchasing Settings Tab - client-managed purchasing reference data
 * (reason codes, contract types, commodity groups/types, etc.).
 * CR-5888: commodity management was relocated here from Platform Admin so
 * client admins can maintain it alongside the other PO reference lists.
 */

import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { ReasonCodesSection } from './ReasonCodesSection';
import { PurchasingEmailNotificationsSection } from './PurchasingEmailNotificationsSection';
import { OpenOrdersSection } from './OpenOrdersSection';
import { SupplierDeclineSetting } from './SupplierDeclineSetting';
import { ConstructionModulesSetting } from './ConstructionModulesSetting';
import { AutoApproveDeliveryAppealSection } from './AutoApproveDeliveryAppealSection';
import { AutoRaiseDeliveryAppealSection } from './AutoRaiseDeliveryAppealSection';
import { OtifSundayRuleSection } from './OtifSundayRuleSection';
import { ProdumaxExportSection } from './ProdumaxExportSection';
import { ContractTypesSection } from './ContractTypesSection';
import { PriceBookSharingSection } from './PriceBookSharingSection';
import { CommodityGroupsSection } from './CommodityGroupsSection';

export function PurchasingTab() {
  // CR-6723: the Produmax ASN Export settings are only relevant when the
  // Call-Off module is enabled (per-supplier call_off_enabled relationships,
  // surfaced as the switch_call_off_tab feature flag). Hide the section when
  // the module is off; the stored asn_produmax_export flag is untouched so
  // re-enabling Call-Off restores the section without reconfiguration.
  const { isFeatureEnabled } = useMenuSettings();
  const showProdumaxExport = isFeatureEnabled('switch_call_off_tab');

  return (
    <div className="space-y-6">
      {/* Email Notifications (CR-4421) */}
      <PurchasingEmailNotificationsSection />

      {/* CR-5892: Allow Supplier Decline — relocated from Platform Admin
          (Helix-only) so client admins control supplier order-decline for
          their own tenant. Stored per-company; the supplier portal enforces it. */}
      <SupplierDeclineSetting />

      {/* Construction tenants switch their own modules on and off here. */}
      <ConstructionModulesSetting />

      {/* Open Orders visibility (CR-4422) */}
      <OpenOrdersSection />

      {/* Auto Approve Delivery Appeal (CR-4423) */}
      <AutoApproveDeliveryAppealSection />

      {/* Auto Raise Delivery Appeals (CR-7068) */}
      <AutoRaiseDeliveryAppealSection />

      {/* OTIF day counting: calendar vs non-Sunday walk (METRIC-DEFINITIONS Q2) */}
      <OtifSundayRuleSection />

      {/* Produmax ASN Export feature flag (PNA) — CR-6723: only while the
          Call-Off module is enabled for this tenant */}
      {showProdumaxExport && <ProdumaxExportSection />}

      {/* Reason Codes */}
      <ReasonCodesSection />

      {/* CR-1441: Configurable contract types (drive contract auto-numbering) */}
      <ContractTypesSection />

      {/* CR-6346: Price Book Sharing — global flag + which price book types
          (driven by the contract types above) suppliers can view. */}
      <PriceBookSharingSection />

      {/* CR-5888: Manage Commodity Groups and Types — relocated from Platform
          Admin so client admins can maintain them next to the other PO
          reference lists. Backend restricts writes to client admins. */}
      <CommodityGroupsSection />
    </div>
  );
}
